/**
 * JobFlagsCell
 *
 * Compact chip strip designed for the Data View preview table cell.
 * Shows every flag on an org across the selected year list; clicking
 * opens a small popover where the operator picks a year and toggles /
 * annotates flags for that year.
 *
 * Props:
 *   - orgId    (required): organisation id
 *   - years    (required): int[] — the year list currently selected in
 *                           the preview. The picker defaults to the
 *                           first (most-recent) year.
 *   - flags    (required): array of {flag_id, flag_type, year, note} for
 *                           this org across all selected years.
 *   - onChange (required): (updatedFlagsForOrg) => void. Called with
 *                           the merged list after any mutation so the
 *                           parent can keep its `previewFlagsByOrg`
 *                           cache in sync.
 */
import React, { useState, useCallback } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { authJsonFetch } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { X } from 'lucide-react';
import { FLAG_TYPES, FLAG_TYPE_BY_KEY } from './JobFlagsChips';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const JobFlagsCell = ({ orgId, years = [], flags = [], onChange }) => {
  const sortedYears = React.useMemo(
    () => [...(years || [])].sort((a, b) => b - a),
    [years],
  );
  const [pickerYear, setPickerYear] = useState(sortedYears[0] || new Date().getFullYear());
  const [editingNote, setEditingNote] = useState(null); // flag_type
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState('');

  const flagsForPickerYear = React.useMemo(
    () => (flags || []).filter(f => Number(f.year) === Number(pickerYear)),
    [flags, pickerYear],
  );
  const flagByType = React.useMemo(() => {
    const m = {};
    for (const f of flagsForPickerYear) m[f.flag_type] = f;
    return m;
  }, [flagsForPickerYear]);

  const toggleFlag = useCallback(async (flagType) => {
    if (!orgId || !pickerYear) return;
    setSaving(flagType);
    try {
      const existing = flagByType[flagType];
      if (existing) {
        const res = await authJsonFetch(`${API}/job-flags`, {
          method: 'DELETE',
          body: JSON.stringify({ org_id: orgId, year: Number(pickerYear), flag_type: flagType }),
        });
        if (!res.ok) throw new Error();
        const next = flags.filter(f => !(f.flag_type === flagType && Number(f.year) === Number(pickerYear)));
        onChange?.(next);
      } else {
        const res = await authJsonFetch(`${API}/job-flags`, {
          method: 'PUT',
          body: JSON.stringify({ org_id: orgId, year: Number(pickerYear), flag_type: flagType, note: '' }),
        });
        if (!res.ok) throw new Error();
        const j = await res.json();
        onChange?.([...flags, { ...j.flag, year: Number(pickerYear) }]);
      }
    } catch (e) {
      toast.error(`Could not toggle ${flagType}`);
    } finally {
      setSaving('');
    }
  }, [orgId, pickerYear, flagByType, flags, onChange]);

  const saveNote = useCallback(async (flagType, note) => {
    if (!orgId || !pickerYear) return;
    setSaving(flagType);
    try {
      const res = await authJsonFetch(`${API}/job-flags`, {
        method: 'PUT',
        body: JSON.stringify({ org_id: orgId, year: Number(pickerYear), flag_type: flagType, note: note || '' }),
      });
      if (!res.ok) throw new Error();
      const j = await res.json();
      const next = flags
        .filter(f => !(f.flag_type === flagType && Number(f.year) === Number(pickerYear)))
        .concat({ ...j.flag, year: Number(pickerYear) });
      onChange?.(next);
      toast.success('Note saved');
      setEditingNote(null);
    } catch (e) {
      toast.error('Could not save note');
    } finally {
      setSaving('');
    }
  }, [orgId, pickerYear, flags, onChange]);

  // Chip sort — group by year desc, then Marketing → Photography → Production → Financials.
  const chipStrip = React.useMemo(() => {
    const flagOrder = ['marketing', 'photography', 'production', 'financials'];
    return [...(flags || [])].sort((a, b) => {
      const yr = Number(b.year) - Number(a.year);
      if (yr !== 0) return yr;
      return flagOrder.indexOf(a.flag_type) - flagOrder.indexOf(b.flag_type);
    });
  }, [flags]);

  const yy = (yr) => String(yr).slice(-2);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="flex items-center gap-1 min-h-[22px] hover:bg-muted/60 rounded px-1 py-0.5 -mx-1 -my-0.5 max-w-full"
          data-testid="job-flags-cell"
          onClick={(e) => e.stopPropagation()}
          title="Click to edit flags"
        >
          {chipStrip.length === 0 ? (
            <span className="text-[10px] text-muted-foreground/70 italic px-1">+ flag</span>
          ) : (
            chipStrip.map(f => {
              const meta = FLAG_TYPE_BY_KEY[f.flag_type];
              if (!meta) return null;
              const showYear = sortedYears.length > 1;
              return (
                <span
                  key={`${f.flag_type}-${f.year}`}
                  className={cn(
                    'inline-flex items-center rounded-full border text-[10px] font-semibold leading-none',
                    showYear ? 'px-1.5 py-0.5 gap-0.5' : 'w-5 h-5 justify-center',
                    meta.bg,
                  )}
                  title={`${meta.label} · ${f.year}${f.note ? ' · ' + f.note : ''}`}
                >
                  <span>{meta.short}</span>
                  {showYear && <span className="text-[9px] opacity-75">·{yy(f.year)}</span>}
                </span>
              );
            })
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-3"
        side="bottom"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-2" data-testid="job-flags-cell-picker">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold">Flags</span>
            {sortedYears.length > 1 && (
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-muted-foreground">Year</span>
                <Select
                  value={String(pickerYear)}
                  onValueChange={(v) => setPickerYear(Number(v))}
                >
                  <SelectTrigger className="h-6 text-[11px] w-[74px]" data-testid="job-flags-cell-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortedYears.map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {sortedYears.length <= 1 && (
              <span className="text-[11px] text-muted-foreground">{pickerYear}</span>
            )}
          </div>

          {FLAG_TYPES.map(meta => {
            const active = !!flagByType[meta.key];
            return (
              <div key={meta.key} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleFlag(meta.key); }}
                    disabled={saving === meta.key}
                    className={cn(
                      'flex-1 inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs font-medium text-left transition-colors',
                      active ? meta.bg : 'bg-muted/30 text-muted-foreground border-transparent hover:bg-muted',
                    )}
                    data-testid={`job-flags-cell-toggle-${meta.key}`}
                  >
                    <span className={cn(
                      'inline-flex items-center justify-center rounded-full w-5 h-5 text-[10px] font-bold flex-shrink-0',
                      active ? 'bg-white/70 dark:bg-black/30' : '',
                    )}>{meta.short}</span>
                    <span className="flex-1">{meta.label}</span>
                    {active && <span className="text-[10px] opacity-70">on</span>}
                  </button>
                  {active && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setNoteDraft(flagByType[meta.key]?.note || '');
                        setEditingNote(meta.key === editingNote ? null : meta.key);
                      }}
                      className="text-[11px] px-1.5 py-1 rounded hover:bg-muted"
                      data-testid={`job-flags-cell-note-btn-${meta.key}`}
                      title="Add / edit note"
                    >
                      {flagByType[meta.key]?.note ? 'edit note' : '+ note'}
                    </button>
                  )}
                </div>
                {editingNote === meta.key && (
                  <div className="space-y-1 pl-1">
                    <Textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Short note…"
                      rows={2}
                      className="text-xs"
                      data-testid={`job-flags-cell-note-input-${meta.key}`}
                    />
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingNote(null)}
                        className="h-6 text-[11px] px-2"
                      >Cancel</Button>
                      <Button
                        size="sm"
                        onClick={() => saveNote(meta.key, noteDraft)}
                        disabled={saving === meta.key}
                        className="h-6 text-[11px] px-2"
                        data-testid={`job-flags-cell-note-save-${meta.key}`}
                      >Save</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default JobFlagsCell;
